/**
 * Pantalla completa con fallback para iOS.
 *
 * Safari de iOS no soporta la Fullscreen API en elementos que no sean el
 * reproductor de vídeo nativo: `requestFullscreen()` sobre un div o sobre nuestro
 * <video> con controles propios simplemente no existe o pone el vídeo en el
 * reproductor del sistema (que taparía la capa de control táctil). Así que cuando
 * la API real no está, se cae a una "pantalla completa por CSS": una clase que fija
 * el contenedor a todo el viewport. No oculta la barra del navegador en iOS, pero
 * es lo máximo alcanzable y mantiene nuestros controles y el control táctil.
 */

interface FullscreenTarget {
  /** Elemento a maximizar con la API real (Android/desktop). */
  element: HTMLElement;
  /** Contenedor que recibe la clase de pseudo-fullscreen (fallback iOS). */
  container: HTMLElement;
}

const PSEUDO_CLASS = 'pseudo-fs';

function realFullscreenSupported(element: HTMLElement): boolean {
  return typeof element.requestFullscreen === 'function' && document.fullscreenEnabled;
}

export function isFullscreen(target: FullscreenTarget): boolean {
  return (
    document.fullscreenElement === target.element ||
    target.container.classList.contains(PSEUDO_CLASS)
  );
}

export async function enterFullscreen(target: FullscreenTarget): Promise<void> {
  if (realFullscreenSupported(target.element)) {
    try {
      await target.element.requestFullscreen();
      return;
    } catch {
      // Cae al pseudo-fullscreen si la API real rechaza (p. ej. gesto no válido).
    }
  }
  target.container.classList.add(PSEUDO_CLASS);
}

export async function exitFullscreen(target: FullscreenTarget): Promise<void> {
  target.container.classList.remove(PSEUDO_CLASS);
  if (document.fullscreenElement === target.element) {
    await document.exitFullscreen().catch(() => undefined);
  }
}

export async function toggleFullscreen(target: FullscreenTarget): Promise<void> {
  if (isFullscreen(target)) await exitFullscreen(target);
  else await enterFullscreen(target);
}

/** Notifica cambios de estado (incluye la salida por gesto del sistema / Esc). */
export function onFullscreenChange(handler: () => void): () => void {
  document.addEventListener('fullscreenchange', handler);
  return () => document.removeEventListener('fullscreenchange', handler);
}
